import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import { makeStyles } from '@material-ui/core/styles';

import Button from 'components/Button/Button';
import { getRedesignDialogColors } from './redesignDialogColors';
import { getRedesignDialogFocusRing } from './redesignDialogStyles';

/**
 * The house Button's props per kind, the look the recolour starts from: the
 * filled `light` button the redesigns use for their main action, the
 * outlined `input` one, and the danger colour. The tonal kinds and the
 * outlined one have hover fills of their own, so the house darkening layer
 * (the backface) goes.
 */
const KIND_PROPS = {
  primary: { color: 'light' },
  secondary: { color: 'input', outline: true, disableBackface: true },
  danger: { color: 'danger', disableBackface: true },
  success: { color: 'light', disableBackface: true },
};

// Every rule under '&&': the house Button's colour, background and border
// rules are one class each (and its text colour also a dynamic rule).
const useStyles = makeStyles(
  (theme) => {
    const colors = getRedesignDialogColors(theme);
    const focus = { '&.Mui-focusVisible': getRedesignDialogFocusRing(colors) };
    // A status colour on its tinted fill, the fill a step stronger on hover.
    const tonal = (text, fill, fillHover) => ({
      '&&': {
        backgroundColor: fill,
        color: text,
        ...focus,
        '@media (hover: hover)': {
          '&:hover': {
            backgroundColor: fillHover,
            color: text,
          },
        },
      },
    });
    return {
      // Room between a glyph (or ButtonLoading) and the label.
      root: {
        '&&': {
          gap: theme.spacing(1),
        },
      },
      // The accent filled; the house backface darkens it on hover.
      primary: {
        '&&': {
          backgroundColor: colors.accent,
          color: colors.accentText,
          ...focus,
          '&:hover': {
            color: colors.accentText,
          },
        },
      },
      // A neutral outline, the hover fill under the pointer.
      secondary: {
        '&&': {
          backgroundColor: 'transparent',
          color: colors.text,
          borderColor: colors.fieldOutline,
          ...focus,
          '@media (hover: hover)': {
            '&:hover': {
              backgroundColor: colors.hoverFill,
              borderColor: colors.fieldOutlineHover,
              color: colors.text,
            },
          },
        },
      },
      danger: tonal(colors.danger, colors.dangerFill, colors.dangerFillHover),
      success: tonal(
        colors.success,
        colors.successFill,
        colors.successFillHover
      ),
    };
  },
  { name: 'LixRedesignDialogButton' }
);

/**
 * A footer (or section) button of the redesigned dialog: the house Button,
 * rounded and in its sizes, recoloured with the dialog's tokens per `kind` —
 * `'primary'` (the accent, filled), `'secondary'` (a neutral outline, the
 * default), `'danger'` and `'success'` (the status colour on its tinted
 * fill). Every other prop (onClick, disabled, size="small", fullWidth, to,
 * …) goes to the house Button; children may start with an mdi Icon or a
 * ButtonLoading, spaced from the label.
 * @example
 * <RedesignDialogButton kind="primary" onClick={save} disabled={saving}>
 *   {saving ? <ButtonLoading /> : null}
 *   {t('common.save')}
 * </RedesignDialogButton>
 * @param {Object} props
 * @param {'primary'|'secondary'|'danger'|'success'} [props.kind='secondary']
 */
const RedesignDialogButton = React.forwardRef(
  function RedesignDialogButton(props, ref) {
    const { kind, className, ...rest } = props;
    const classes = useStyles();
    const look = KIND_PROPS[kind] ? kind : 'secondary';
    return (
      <Button
        {...rest}
        {...KIND_PROPS[look]}
        ref={ref}
        rounded
        className={clsx(classes.root, classes[look], className)}
        data-redesign-dialog-button={look}
      />
    );
  }
);

RedesignDialogButton.propTypes = {
  kind: PropTypes.oneOf(['primary', 'secondary', 'danger', 'success']),
  className: PropTypes.string,
  children: PropTypes.node,
};

RedesignDialogButton.defaultProps = {
  kind: 'secondary',
};

export default RedesignDialogButton;
